import { selexpNotifiApiDuck, AdminNotificationsTag, userNotificationsTag } from '../selexp-notifi-api-duck';
import {
  EMetadataType,
  EUserNotificationStatus,
  EUserNotificationType,
  TCustomNotification,
  TCustomNotificationResponse,
  TNotificationMetadataResponse,
  TUserNotificationResponse,
} from './types-user-notifications';

export const adminNotificationEndpoint = '/notifications/admin';
export const metadataNotificationEndpoint = '/notifications/metadata';
const virtualListId = 'LIST';

export const userNotificationsApiDuck = selexpNotifiApiDuck.injectEndpoints({
  endpoints: (builder) => ({
    getUserNotifications: builder.query<TUserNotificationResponse, { cacheKey: string; notificationStatus: EUserNotificationStatus }>({
      query: (payload) => ({
        url: '/GetUserNotifications',
        customParams: {
          status: payload.notificationStatus,
        },
      }),
      transformResponse: (response: TUserNotificationResponse) => ({
        ...response,
        notifications: (response?.notifications || []).map((notification) => ({
          ...notification,
          notificationType: notification?.notificationType || EUserNotificationType.ActionCenter,
          notificationStatus: notification?.notificationStatus || EUserNotificationStatus.Active, // Default to Active if not provided
        })),
      }),
      providesTags: [userNotificationsTag],
    }),

    getAdminNotifications: builder.query<TCustomNotification[], void>({
      query: () => adminNotificationEndpoint,
      transformResponse: (response: TCustomNotificationResponse) => response?.notifications || [],
      // We provide the tags for each notification for cache invalidation of specific notifications. (or full list)
      providesTags: (result) =>
        result
          ? [
              ...result.map(
                (notification) =>
                  ({
                    type: AdminNotificationsTag,
                    id: notification.id,
                  }) as const
              ),
              { type: AdminNotificationsTag, id: virtualListId },
            ]
          : [{ type: AdminNotificationsTag, id: virtualListId }],
    }),

    addAdminNotification: builder.mutation<TCustomNotificationResponse, TCustomNotification>({
      query: (notification) => ({
        url: adminNotificationEndpoint,
        method: 'POST',
        body: notification,
      }),
      async onQueryStarted(notification, { dispatch, queryFulfilled }) {
        try {
          const { data: createdNotifications } = await queryFulfilled;
          const createdNotification = createdNotifications.notifications[0];
          dispatch(
            selexpNotifiApiDuck.util.updateQueryData(
              'getAdminNotifications' as never,
              undefined as never,
              (currentNotifications: TCustomNotification[]) => {
                currentNotifications.push(createdNotification);
              }
            )
          );
        } catch {
          // Handle error
        }
      },
    }),

    deleteAdminNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: `${adminNotificationEndpoint}/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            selexpNotifiApiDuck.util.updateQueryData(
              'getAdminNotifications' as never,
              undefined as never,
              (currentNotifications: TCustomNotification[]) => {
                const index = currentNotifications.findIndex((notification) => notification.id === id);
                if (index !== -1) {
                  currentNotifications.splice(index, 1);
                }
              }
            )
          );
        } catch {
          // Handle error
        }
      },
    }),

    updateAdminNotification: builder.mutation<TCustomNotificationResponse, TCustomNotification>({
      query: (notification) => ({
        url: `${adminNotificationEndpoint}/${notification.id}`,
        method: 'PUT',
        body: notification,
      }),
      async onQueryStarted(notification, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedNotifications } = await queryFulfilled;
          const updatedNotification = updatedNotifications.notifications[0];
          dispatch(
            selexpNotifiApiDuck.util.updateQueryData(
              'getAdminNotifications' as never,
              undefined as never,
              (currentNotifications: TCustomNotification[]) => {
                const index = currentNotifications.findIndex((n) => n.id === updatedNotification.id);
                if (index !== -1) {
                  currentNotifications[index] = updatedNotification;
                }
              }
            )
          );
        } catch {
          // Handle error
        }
      },
    }),

    getNotificationMetadata: builder.query<TNotificationMetadataResponse, { fiscalYear: string; metaDataType?: EMetadataType }>({
      query: (payload) => ({
        url: payload.metaDataType ? `${metadataNotificationEndpoint}/${payload.metaDataType}` : '/metadata',
        noFilters: true,
        customParams: {
          fiscalYearId: payload.fiscalYear,
        },
      }),
    }),
  }),
});

export const {
  useGetUserNotificationsQuery,
  useLazyGetUserNotificationsQuery,
  useAddAdminNotificationMutation,
  useDeleteAdminNotificationMutation,
  useGetAdminNotificationsQuery,
  useUpdateAdminNotificationMutation,
  useGetNotificationMetadataQuery,
} = userNotificationsApiDuck;
